import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LinkButton from 'flarum/common/components/LinkButton';
import ItemList from 'flarum/common/utils/ItemList';
import icon from 'flarum/common/helpers/icon';
import listItems from 'flarum/common/helpers/listItems';
import PictureList from '../components/PictureList';
import PictureSortDropdown from '../components/PictureSortDropdown';
import PictureListState from '../states/PictureListState';
import UploadModal from '../modals/UploadModal';
import Uploader from '../states/Uploader';
import Album from '../models/Album';
import Picture from '../models/Picture';

const translationPrefix = 'kilowhat-cimaise.forum.album-show.';

function cleanRelationship(pictures: false | (Picture | undefined)[]): Picture[] {
    // @ts-ignore Somehow typescript doesn't detect that "undefined" is no longer possible
    return (pictures || []).filter(picture => typeof picture !== 'undefined');
}

export default class AlbumShowPage extends Page {
    albumId!: string
    album?: Album
    uploader!: Uploader

    oninit(vnode: any) {
        super.oninit(vnode);

        this.albumId = m.route.param('id').split('-')[0];
        this.uploader = new Uploader(this.albumId);

        const pictureListParams = {
            q: '-is:draft album:' + this.albumId,
        };

        let freshListState: PictureListState | null = null;
        let freshListStateWillBeFilledWithFirstPage: boolean = false;

        // Create new picture list, but keep existing one if it's for the same album (happens if we come back from a picture)
        if (!app.cache.cimaiseAlbumPictures || app.cache.cimaiseAlbumPictures.params.q !== pictureListParams.q) {
            freshListState = new PictureListState(pictureListParams);
        }

        const preloadedAlbum = app.preloadedApiDocument<Album>();

        if (preloadedAlbum) {
            this.show(preloadedAlbum);
        } else {
            const album = app.store.getById<Album>('cimaise-albums', this.albumId);

            if (!album || album.picturesFirstPage() === false) {
                freshListStateWillBeFilledWithFirstPage = true;

                // If we have the album in cache but it doesn't have the first page of results, it means it was never loaded from AlbumShowController
                // In that case we'll reload it completely to get access to the album.discussions relationship
                app.store.find<Album>('cimaise/albums', this.albumId).then(album => {
                    this.show(album);

                    if (freshListState) {
                        freshListState.parseResults(cleanRelationship(album.picturesFirstPage()));
                        freshListState.loading = false;
                    }

                    m.redraw();
                });
            } else {
                this.show(album);
            }
        }

        if (freshListState) {
            if (preloadedAlbum) {
                // When there's a preloaded album, it contains a pseudo-pre-loaded list of pictures
                // When this happens we directly inject the data in the state
                freshListState.parseResults(cleanRelationship(preloadedAlbum.picturesFirstPage()));
            } else if (freshListStateWillBeFilledWithFirstPage) {
                freshListState.loading = true;
            } else {
                freshListState.refresh();
            }

            app.cache.cimaiseAlbumPictures = freshListState;
        }
    }

    show(album: Album) {
        this.album = album;
        this.uploader.supportsSilentPublish = (this.album.discussions() || []).length > 0;
    }

    heroItems() {
        const items = new ItemList();

        if (!this.album) {
            return items;
        }

        items.add('title', m('h2', this.album.title()));

        const user = this.album.user();

        items.add('type', m('span', this.album.isCommunity() ? [
            icon('fas fa-user-tag'),
            ' ',
            app.translator.trans(translationPrefix + 'community'),
        ] : [
            icon('fas fa-users'),
            ' ',
            app.translator.trans(translationPrefix + 'personal', {
                user,
                a: user ? m(Link, {
                    href: app.route.user(user),
                }) : m('span'),
            }),
        ]));

        if (this.album.description()) {
            items.add('description', m('p', this.album.description()));
        }

        const discussions = (this.album.discussions() || []);

        if (discussions.length) {
            discussions.forEach(discussion => {
                if (!discussion) {
                    return;
                }

                items.add('discussion-' + discussion.id(), LinkButton.component({
                    className: 'Button Button--link',
                    href: app.route.discussion(discussion),
                    icon: 'fas fa-comment',
                }, discussions.length === 1 && discussion.title() === this.album!.title() ?
                    // If a single discussion is linked and share the same title, use generic translatable text
                    app.translator.trans(translationPrefix + 'discussion') :
                    // Otherwise the link will use the discussion title as label
                    discussion.title()));
            });
        }

        return items;
    }

    view() {
        if (!this.album) {
            return m('.container', m('p', app.translator.trans(translationPrefix + 'loading')));
        }

        const coverPicture = this.album.coverPicture();

        return m('div', [
            m('header.Hero.AlbumHero', [
                m('.container', m('ul', listItems(this.heroItems().toArray()))),
                m('.AlbumHeroBackground', {
                    style: coverPicture ? {
                        backgroundImage: 'url(' + coverPicture.imageUrl(150) + ')',
                    } : {},
                }),
            ]),
            m('.container', [
                m('.AlbumPageControls', [
                    m(PictureSortDropdown, {
                        sort: app.cache.cimaiseAlbumPictures.params.sort,
                        onchange: (value: string) => {
                            app.cache.cimaiseAlbumPictures.params.sort = value;
                            app.cache.cimaiseAlbumPictures.refresh();
                        },
                    }),
                    ' ',
                    this.album.canUpload() ? Button.component({
                        className: 'Button Button--primary',
                        onclick: () => {
                            app.modal.show(UploadModal, {
                                uploader: this.uploader,
                                onsubmit: () => {
                                    app.cache.cimaiseAlbumPictures.refresh();
                                },
                            });
                        },
                    }, app.translator.trans(translationPrefix + 'upload')) : null,
                ]),
                m(PictureList, {
                    state: app.cache.cimaiseAlbumPictures,
                    thumbnailAttrs: {
                        showUser: true,
                    },
                }),
            ]),
        ]);
    }
}
